import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AppLayout } from '../components/AppLayout'
import { creditApi, type CreditState, type CreditOrderRow, type FinancingTermsResponse } from '../api/endpoints'
import { watchAuthState } from '../lib/firebase'
import { useApp } from '../store'

/**
 * /credit — buyer's per-order credit page.
 *
 * Mirrors Heera's Admin/MyCredit.cshtml semantics on the platform's
 * Firestore-backed shape:
 *   - Aggregate: limit / used / available + progress bar.
 *   - Per-order rows with each order's own due date + days-to-due
 *     + status pill (overdue / due-soon / on-time).
 *   - "Make a payment" modal per row (matches SP error codes).
 *   - "Request higher limit" modal (one open at a time).
 *   - Pending-payments card (admin not yet confirmed).
 *
 * Per-order cycle: see project_credit_per_order_cycle.md — each order
 * has its own clock; the page surfaces that explicitly instead of a
 * single misleading account-wide due date.
 */

const fmt = (n: number) => `$${Math.round(Math.max(0, n)).toLocaleString()}`
const fmtDate = (iso: string) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// SP-style error code → human message. Matches Heera's MyCredit.cshtml
// error map verbatim so the buyer sees the same copy on either surface.
const ERROR_MESSAGES: Record<string, string> = {
  OrderNotFound: 'That order is no longer in your account.',
  NotYourOrder: 'You can only pay your own orders.',
  AmountMustBePositive: 'Enter an amount greater than $0.',
  AmountExceedsBalance: 'That amount is more than the order balance.',
  AlreadyPending: 'You already have an open limit request — wait for that one to be reviewed.',
  NotAnIncrease: 'The requested limit must be higher than your current limit.',
  ServerError: 'Something went wrong on our side. Try again in a moment.',
  Unknown: 'Could not complete the request.',
}

export function Credit() {
  const [state, setState] = useState<CreditState | null>(null)
  const [financing, setFinancing] = useState<FinancingTermsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [flashMessage, setFlashMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null)
  const [financeOpen, setFinanceOpen] = useState(false)

  // Pay modal state
  const [payOrderId, setPayOrderId] = useState<number | null>(null)
  const [payOrderBalance, setPayOrderBalance] = useState(0)
  // Limit modal state
  const [limitOpen, setLimitOpen] = useState(false)

  async function load() {
    try {
      const s = await creditApi.state()
      setState(s)
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Could not load credit info')
    } finally {
      setLoading(false)
    }
    // Financing terms — best-effort, its own catch so it never blocks the page.
    creditApi.financing().then(setFinancing).catch(() => { /* ignore */ })
  }

  // Wait for Firebase to settle the auth state BEFORE firing the API call.
  // Without this, a hard refresh on /credit fires the request before
  // currentUser is populated → 401 "Missing Authorization: Bearer <id-token>".
  // We re-load on every auth-state change so signing in/out updates the
  // page without a manual refresh.
  useEffect(() => {
    const unsub = watchAuthState((user) => {
      if (user) {
        load()
      } else {
        setLoading(false)
        setError('Sign in to view your credit account.')
      }
    })
    return () => unsub()
  }, [])

  function flash(kind: 'success' | 'danger', text: string) {
    setFlashMessage({ kind, text })
    setTimeout(() => setFlashMessage(null), 5000)
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-4xl px-6 py-8 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight">Credit account</h1>
          <p className="mt-1 text-sm text-muted">
            Each order has its own payment window — pay any one off without affecting the others.
          </p>
        </div>

        <AnimatePresence>
          {flashMessage && (
            <motion.div
              key={flashMessage.text}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className={`rounded-card border p-3 text-sm ${
                flashMessage.kind === 'success'
                  ? 'border-success/30 bg-success/5 text-success'
                  : 'border-danger/30 bg-danger/5 text-danger'
              }`}
            >
              {flashMessage.text}
            </motion.div>
          )}
        </AnimatePresence>

        {financing && (financing.live || financing.approved) && (
          <FinancingCard
            financing={financing}
            stateFinancing={state?.financing ?? null}
            onRequest={() => setFinanceOpen(true)}
          />
        )}

        {loading ? (
          <div className="rounded-3xl bg-paper border border-border h-44 animate-pulse" />
        ) : error ? (
          <div className="rounded-card border border-danger/30 bg-danger/5 text-danger text-sm p-4">{error}</div>
        ) : !state || state.hasCreditAccount === false ? (
          <NoCreditAccount />
        ) : (
          <>
            <Aggregate
              state={state}
              onRequestIncrease={() => setLimitOpen(true)}
            />
            {state.hasOverdue && (
              <div className="rounded-card border border-danger/30 bg-danger/5 text-danger text-sm p-3">
                <strong>You have overdue invoices.</strong> Settle them to avoid further holds on your account.
              </div>
            )}
            {(state.creditLimit ?? 0) > 0 && (state.availableCredit ?? 0) <= 0 && (
              <div className="rounded-card border border-warn/40 bg-warn/10 text-warn text-sm p-3">
                <strong>Credit limit reached.</strong> New orders are blocked until you pay down or request a higher limit.
              </div>
            )}
            <Outstanding
              orders={state.orders || []}
              onPay={(orderId, balance) => {
                setPayOrderId(orderId)
                setPayOrderBalance(balance)
              }}
            />
            {state.pendingPayments && state.pendingPayments.length > 0 && (
              <PendingPayments payments={state.pendingPayments} />
            )}
          </>
        )}
      </div>

      <PayModal
        open={payOrderId !== null}
        orderId={payOrderId}
        balance={payOrderBalance}
        onClose={() => setPayOrderId(null)}
        onSuccess={() => {
          setPayOrderId(null)
          flash('success', 'Payment logged. DiaEx will confirm receipt and update the order balance shortly.')
          load()
          // The payment changes order balances — bust /orders' 60s TTL so a
          // tab-back there refetches instead of rendering the stale cache.
          useApp.getState().invalidate('orders')
        }}
      />
      <LimitModal
        open={limitOpen}
        onClose={() => setLimitOpen(false)}
        onSuccess={() => {
          setLimitOpen(false)
          flash('success', 'Request submitted. DiaEx will email you once it\'s reviewed.')
        }}
      />
      <FinanceModal
        open={financeOpen}
        onClose={() => setFinanceOpen(false)}
        onSuccess={() => {
          setFinanceOpen(false)
          flash('success', 'Financing request submitted. DiaEx will review and email you once approved.')
          load()
        }}
      />
    </AppLayout>
  )
}

/**
 * Financing terms + standing (Revenue Stream 3, #64). Shows the APR, grace
 * window, interest accrued to date + next interest date; a not-yet-approved
 * buyer sees a "Request Aska Credit" CTA.
 */
function FinancingCard({ financing, stateFinancing, onRequest }: {
  financing: FinancingTermsResponse
  stateFinancing: CreditState['financing'] | null
  onRequest: () => void
}) {
  const approved = financing.approved
  const enrolled = financing.enrolled
  const pending = financing.requestPending || (enrolled && !approved)
  const accrued = stateFinancing?.interestAccrued ?? 0
  const nextDate = stateFinancing?.nextInterestDate ?? null
  // Three states: approved (active) · enrolled-pending-approval · not enrolled.
  const badge = approved ? { text: 'Approved', cls: 'bg-success/10 text-success' }
    : pending ? { text: 'Pending approval', cls: 'bg-gold/10 text-gold' }
    : { text: 'Not enrolled', cls: 'bg-page text-soft' }
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="card space-y-4"
      data-testid="financing-card"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-ink">Aska Credit — financing</h3>
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${badge.cls}`}>
          {badge.text}
        </span>
      </div>
      <p className="text-sm text-muted leading-relaxed">
        <strong>Aska Credit</strong> (part of Aska Services) lets you buy now and pay later on eligible
        storefront orders: <strong>{financing.graceDays}-day interest-free</strong>, then <strong>{financing.apr}% APR</strong> on
        the outstanding balance. No card is charged at checkout — the amount is added to your account balance.
      </p>
      <div className="grid grid-cols-3 gap-4">
        <Stat label="APR" value={`${financing.apr}%`} />
        <Stat label="Interest-free" value={`${financing.graceDays} days`} />
        <Stat label="Interest accrued" value={fmt(accrued)} kind={accrued > 0 ? 'danger' : undefined} />
      </div>
      {approved ? (
        nextDate && (
          <p className="text-xs text-muted">
            Next interest accrues on <strong className="text-ink">{fmtDate(nextDate)}</strong> if a financed balance is still open.
          </p>
        )
      ) : pending ? (
        // gold-600 (contrast-safe on cream) — `gold-dark` was never a defined
        // token, so this line silently rendered in inherited navy.
        <p className="text-xs text-gold-600 bg-gold/5 border border-gold/20 rounded-md px-3 py-2">
          You’re enrolled in Aska Credit — your application is under review. We’ll email you once it’s approved.
        </p>
      ) : (
        <button
          type="button"
          onClick={onRequest}
          disabled={!financing.live}
          className="bg-primary hover:bg-[#163f73] text-white text-xs font-semibold px-3 py-2 rounded-md disabled:bg-soft disabled:cursor-not-allowed"
          data-testid="request-financing"
        >
          {financing.live ? 'Sign up for Aska Credit →' : 'Aska Credit coming soon'}
        </button>
      )}
    </motion.section>
  )
}

function NoCreditAccount() {
  return (
    <div className="card space-y-3">
      <p className="text-base font-semibold text-ink">No credit account on file</p>
      <p className="text-sm text-muted leading-relaxed">
        You're set up as a cash buyer — orders are paid up front via card.
        To enable net terms, contact{' '}
        <a className="text-primary underline" href="mailto:info@thediaex.com">info@thediaex.com</a>
        {' '}and our team will assess your account.
      </p>
    </div>
  )
}

function Aggregate({ state, onRequestIncrease }: {
  state: CreditState
  onRequestIncrease: () => void
}) {
  const limit = state.creditLimit ?? 0
  const used = state.usedCredit ?? 0
  const available = state.availableCredit ?? 0
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0
  const over = pct >= 90

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="card space-y-4"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-ink">Account credit summary</h3>
        <button
          type="button"
          onClick={onRequestIncrease}
          className="text-xs font-semibold text-primary hover:underline"
        >
          Request a higher limit →
        </button>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Credit limit" value={fmt(limit)} />
        <Stat label="Used" value={fmt(used)} />
        <Stat label="Available" value={fmt(available)} kind="success" />
      </div>
      <div className="h-2 rounded-full bg-page overflow-hidden">
        <motion.div
          className={`h-full ${over ? 'bg-gradient-to-r from-danger to-[#e74c3c]' : 'bg-gradient-to-r from-primary to-[#2a6cb8]'}`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </motion.section>
  )
}

function Stat({ label, value, kind }: { label: string; value: string; kind?: 'success' | 'danger' }) {
  const valueClass = kind === 'success' ? 'text-success' : kind === 'danger' ? 'text-danger' : 'text-ink'
  return (
    <div className="rounded-xl border border-line bg-paper px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-soft">{label}</p>
      <p className={`mt-1 text-xl font-bold ${valueClass}`}>{value}</p>
    </div>
  )
}

function Outstanding({ orders, onPay }: {
  orders: CreditOrderRow[]
  onPay: (orderId: number, balance: number) => void
}) {
  if (orders.length === 0) {
    return (
      <section className="card text-center py-8">
        <p className="text-base font-semibold text-ink">All clear</p>
        <p className="mt-1 text-sm text-muted">No outstanding balances.</p>
      </section>
    )
  }

  return (
    <section className="card space-y-3">
      <h3 className="text-sm font-semibold text-ink">Outstanding orders</h3>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-soft border-b border-border">
              <th className="py-2 px-2 font-semibold">Order</th>
              <th className="py-2 px-2 font-semibold">Placed</th>
              <th className="py-2 px-2 font-semibold">Due</th>
              <th className="py-2 px-2 font-semibold">Balance</th>
              <th className="py-2 px-2 font-semibold">Status</th>
              <th className="py-2 px-2 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const overdue = o.isOverdue
              const dueSoon = !overdue && o.daysToDue <= 5
              const pillClass = overdue
                ? 'bg-danger/10 text-danger'
                : dueSoon
                  ? 'bg-warn/15 text-warn'
                  : 'bg-success/10 text-success'
              const pillText = overdue
                ? `${Math.abs(o.daysToDue)} day${Math.abs(o.daysToDue) === 1 ? '' : 's'} overdue`
                : o.daysToDue === 0
                  ? 'Due today'
                  : `Due in ${o.daysToDue} day${o.daysToDue === 1 ? '' : 's'}`
              return (
                <tr key={o.orderId} className={`border-b border-[#f0f2f6] ${overdue ? 'border-l-2 border-l-danger' : ''}`}>
                  <td className="py-3 px-2 font-bold text-ink">#{o.orderId}</td>
                  <td className="py-3 px-2 text-muted">{fmtDate(o.orderDate)}</td>
                  <td className="py-3 px-2 text-muted">{fmtDate(o.dueDate)}</td>
                  <td className="py-3 px-2">
                    <span className="font-semibold text-ink">{fmt(o.balAmount)}</span>
                    <span className="text-soft text-xs ml-1">of {fmt(o.amount)}</span>
                  </td>
                  <td className="py-3 px-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pillClass}`}>
                      {pillText}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <button
                      type="button"
                      onClick={() => onPay(o.orderId, o.balAmount)}
                      className="bg-primary hover:bg-[#163f73] text-white text-xs font-semibold px-3 py-1.5 rounded-md"
                    >
                      Pay
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function PendingPayments({ payments }: { payments: CreditState['pendingPayments'] }) {
  return (
    <section className="card space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-ink">Awaiting confirmation</h3>
        <p className="mt-0.5 text-xs text-muted">
          Payments you've logged that admin hasn't reconciled yet.
        </p>
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-soft border-b border-border">
              <th className="py-2 px-2 font-semibold">Order</th>
              <th className="py-2 px-2 font-semibold">Amount</th>
              <th className="py-2 px-2 font-semibold">Method</th>
              <th className="py-2 px-2 font-semibold">Reference</th>
              <th className="py-2 px-2 font-semibold">Logged</th>
            </tr>
          </thead>
          <tbody>
            {(payments || []).map((p) => (
              <tr key={p.paymentId} className="border-b border-[#f0f2f6]">
                <td className="py-2 px-2 font-bold text-ink">#{p.orderId}</td>
                <td className="py-2 px-2 text-ink">{fmt(p.amount)}</td>
                <td className="py-2 px-2 text-muted">{p.method}</td>
                <td className="py-2 px-2 text-muted">{p.reference || '—'}</td>
                <td className="py-2 px-2 text-muted">{fmtDate(p.ts)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ---------- Modals ----------

function ModalShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <motion.div
      className="fixed inset-0 z-[1050] flex items-center justify-center bg-navy/55 px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 6 }}
        className="w-full max-w-md rounded-2xl bg-paper p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
    </motion.div>
  )
}

function PayModal({ open, orderId, balance, onClose, onSuccess }: {
  open: boolean
  orderId: number | null
  balance: number
  onClose: () => void
  onSuccess: () => void
}) {
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<'BankTransfer' | 'Card' | 'CashAtOffice' | 'Other'>('BankTransfer')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setAmount(balance.toFixed(2))
      setMethod('BankTransfer')
      setReference('')
      setNotes('')
      setError(null)
      setSubmitting(false)
    }
  }, [open, balance])

  async function submit() {
    setError(null)
    const num = Number(amount)
    if (!num || num <= 0) {
      setError('Enter a positive amount.')
      return
    }
    if (num > balance + 0.0001) {
      setError('Amount cannot exceed the order balance.')
      return
    }
    if (orderId === null) return
    setSubmitting(true)
    try {
      await creditApi.logPayment({
        orderId,
        amount: num,
        method,
        reference: reference || null,
        notes: notes || null,
      })
      onSuccess()
    } catch (e: any) {
      const code = e?.body?.error || 'Unknown'
      setError(ERROR_MESSAGES[code] || e?.message || ERROR_MESSAGES.Unknown)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <ModalShell onClose={onClose}>
          <h3 className="text-lg font-bold text-ink">Make a payment</h3>
          <p className="mt-1 text-xs text-muted">
            Order #{orderId} · balance {fmt(balance)}
          </p>

          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Amount (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-ink"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Payment method</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value as typeof method)}
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-ink"
              >
                <option value="BankTransfer">Bank transfer / wire</option>
                <option value="Card">Credit card</option>
                <option value="CashAtOffice">Cash / at office</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Reference (check #, wire ref)</label>
              <input
                type="text"
                maxLength={120}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-ink"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Notes (optional)</label>
              <textarea
                rows={2}
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-ink resize-none"
              />
            </div>
            {error && (
              <div className="rounded-md bg-danger/10 border border-danger/30 text-danger text-sm px-3 py-2">{error}</div>
            )}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-soft border border-border rounded-md hover:bg-page"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-[#163f73] disabled:bg-soft disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving…' : 'Log payment'}
            </button>
          </div>
        </ModalShell>
      )}
    </AnimatePresence>
  )
}

function LimitModal({ open, onClose, onSuccess }: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const [requested, setRequested] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setRequested('')
      setReason('')
      setError(null)
      setSubmitting(false)
    }
  }, [open])

  async function submit() {
    setError(null)
    const num = Number(requested)
    if (!num || num <= 0) {
      setError('Enter a positive amount.')
      return
    }
    setSubmitting(true)
    try {
      await creditApi.requestLimit({ requestedLimit: num, reason: reason || null })
      onSuccess()
    } catch (e: any) {
      const code = e?.body?.error || 'Unknown'
      setError(ERROR_MESSAGES[code] || e?.message || ERROR_MESSAGES.Unknown)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <ModalShell onClose={onClose}>
          <h3 className="text-lg font-bold text-ink">Request a higher credit limit</h3>
          <p className="mt-1 text-xs text-muted">DiaEx reviews these manually. We'll email you once a decision is made.</p>

          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Requested limit (USD)</label>
              <input
                type="number"
                min="0"
                step="100"
                value={requested}
                onChange={(e) => setRequested(e.target.value)}
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-ink"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Why are you requesting this?</label>
              <textarea
                rows={3}
                maxLength={2000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="A few lines on volume / business need."
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-ink resize-none"
              />
            </div>
            {error && (
              <div className="rounded-md bg-danger/10 border border-danger/30 text-danger text-sm px-3 py-2">{error}</div>
            )}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-soft border border-border rounded-md hover:bg-page"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-[#163f73] disabled:bg-soft disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending…' : 'Submit request'}
            </button>
          </div>
        </ModalShell>
      )}
    </AnimatePresence>
  )
}

function FinanceModal({ open, onClose, onSuccess }: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const [reason, setReason] = useState('')
  const [requestedLimit, setRequestedLimit] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setReason('')
      setRequestedLimit('')
      setError(null)
      setSubmitting(false)
    }
  }, [open])

  async function submit() {
    setError(null)
    setSubmitting(true)
    try {
      await creditApi.requestFinancing({
        reason: reason || null,
        requestedLimit: requestedLimit ? Number(requestedLimit) : null,
      })
      onSuccess()
    } catch (e: any) {
      const code = e?.body?.error || 'Unknown'
      setError(ERROR_MESSAGES[code] || e?.message || ERROR_MESSAGES.Unknown)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <ModalShell onClose={onClose}>
          <h3 className="text-lg font-bold text-ink">Request Aska Credit</h3>
          <p className="mt-1 text-xs text-muted">
            DiaEx reviews financing manually. Once approved you can pay for eligible orders
            on credit — interest-free during the grace window, then a set APR on the balance.
          </p>

          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Requested credit limit (USD, optional)</label>
              <input
                type="number"
                min="0"
                step="1000"
                value={requestedLimit}
                onChange={(e) => setRequestedLimit(e.target.value)}
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-ink"
                data-testid="financing-limit-input"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-soft mb-1">Tell us about your business</label>
              <textarea
                rows={3}
                maxLength={2000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Volume, monthly spend, how long you've been buying."
                className="w-full rounded-md border border-border px-3 py-2 text-sm text-ink resize-none"
              />
            </div>
            {error && (
              <div className="rounded-md bg-danger/10 border border-danger/30 text-danger text-sm px-3 py-2">{error}</div>
            )}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-soft border border-border rounded-md hover:bg-page"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-[#163f73] disabled:bg-soft disabled:cursor-not-allowed"
              data-testid="financing-submit"
            >
              {submitting ? 'Sending…' : 'Submit request'}
            </button>
          </div>
        </ModalShell>
      )}
    </AnimatePresence>
  )
}
